import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';

import type { ExpiringProposal } from '@control-center/shared';
import { formatBR, formatRelativeDate, todayLocal } from '@control-center/shared';

import { formatProposalMoney, validityClass, validityFlag } from '../../../lib/proposal-helpers';
import { extractErrorMessage } from '../../../lib/errors';
import { useAcceptCrmProposal, useDuplicateCrmProposal } from '../../../hooks/use-crm-proposals';
import { crmPipelineKeys } from '../../../hooks/use-crm-pipeline';

interface ExpiringProposalsTableProps {
  proposals: ExpiringProposal[];
  userNames: Map<string, string>;
  /** Ações rápidas (duplicar/aceitar) só pra admin/comercial — AC8. */
  canAct: boolean;
}

/**
 * Tabela "Propostas a vencer" (Story 11.12 AC5) — ordenada por validUntil ASC
 * (vem do backend). Ações inline "Renovar/Duplicar" (reusa POST .../duplicate da
 * 11.7) e "Aceitar" (POST .../accept da 11.7). "Dias restantes" calculado no
 * front com todayLocal() (disciplina de timezone).
 */
export function ExpiringProposalsTable({
  proposals,
  userNames,
  canAct,
}: ExpiringProposalsTableProps): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const duplicate = useDuplicateCrmProposal();
  const accept = useAcceptCrmProposal();
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const today = todayLocal();

  const refreshDashboard = (): void => {
    void qc.invalidateQueries({ queryKey: crmPipelineKeys.all });
  };

  const handleDuplicate = (id: string): void => {
    setActingId(id);
    setActionError(null);
    duplicate.mutate(id, {
      onSuccess: (created) => {
        refreshDashboard();
        navigate(`/crm/proposals/${created.id}`);
      },
      onError: (err) => setActionError(extractErrorMessage(err, 'Não foi possível duplicar.')),
      onSettled: () => setActingId(null),
    });
  };

  const handleAccept = (id: string): void => {
    setActingId(id);
    setActionError(null);
    accept.mutate(id, {
      onSuccess: () => refreshDashboard(),
      onError: (err) => setActionError(extractErrorMessage(err, 'Não foi possível aceitar.')),
      onSettled: () => setActingId(null),
    });
  };

  return (
    <section className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">Propostas a vencer</h3>
        <button
          type="button"
          onClick={() => navigate('/crm/proposals')}
          className="text-xs text-link transition-colors hover:underline"
        >
          Ver todas as propostas
        </button>
      </header>

      {actionError && (
        <div className="border-b border-bad/40 bg-bad/10 px-4 py-2 text-xs text-bad">
          {actionError}
        </div>
      )}

      {proposals.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted">
          Nenhuma proposta vence nos próximos 7 dias. Pipeline em dia.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-muted-3">
                <th className="px-4 py-3 font-medium">Número</th>
                <th className="px-4 py-3 font-medium">Destinatário</th>
                <th className="px-4 py-3 font-medium">Produto</th>
                <th className="px-4 py-3 font-medium">Valor</th>
                <th className="px-4 py-3 font-medium">Validade</th>
                <th className="px-4 py-3 font-medium">Responsável</th>
                {canAct && <th className="px-4 py-3 font-medium text-right">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {proposals.map((p) => {
                const flag = validityFlag(p.valid_until, today);
                const remaining = formatRelativeDate(p.valid_until, { nowYMD: today });
                const recipient = p.client?.name ?? p.lead?.name ?? p.deal?.title ?? '—';
                const responsibleName = p.responsible_user_id
                  ? (userNames.get(p.responsible_user_id) ?? '—')
                  : '—';
                const busy = actingId === p.id;
                return (
                  <tr
                    key={p.id}
                    className="border-b border-line/60 transition-colors last:border-0 hover:bg-panel-2"
                  >
                    <td className="px-4 py-3 font-medium tabular-nums text-ink">
                      <button
                        type="button"
                        onClick={() => navigate(`/crm/proposals/${p.id}`)}
                        className="text-left transition-colors hover:text-link"
                      >
                        {p.proposal_number}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-muted-2">{recipient}</td>
                    <td className="px-4 py-3 text-muted-2">{p.saas_product?.name ?? '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-muted">
                      {formatProposalMoney(p.monthly_value)}
                    </td>
                    <td className={`px-4 py-3 tabular-nums ${validityClass(flag)}`}>
                      {formatBR(p.valid_until)}
                      <span className="ml-1 text-[11px]">({remaining})</span>
                    </td>
                    <td className="px-4 py-3 text-muted-2">{responsibleName}</td>
                    {canAct && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1.5">
                          <ActionButton
                            label="Aceitar"
                            icon={<Check size={13} strokeWidth={2} />}
                            disabled={busy}
                            onClick={() => handleAccept(p.id)}
                          />
                          <ActionButton
                            label="Renovar"
                            icon={<Copy size={13} strokeWidth={2} />}
                            disabled={busy}
                            onClick={() => handleDuplicate(p.id)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ActionButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: JSX.Element;
  onClick: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded-[8px] border border-line px-2 py-1 text-[12px] text-muted transition-colors hover:border-line-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
    >
      {icon}
      {label}
    </button>
  );
}
